import Axios from "axios";
import API_CONFIG from "./apiConfig";
import _ from "lodash";

const extractFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== "string") return "";

  // RFC 5987: filename*=UTF-8''...
  const filenameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8''|utf-8'')?([^;\n]+)/);
  if (filenameStarMatch && filenameStarMatch[1]) {
    const raw = filenameStarMatch[1].trim().replace(/(^")|("$)/g, "");
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }

  const filenameMatch = contentDisposition.match(/filename=([^;\n]+)/);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].trim().replace(/(^")|("$)/g, "");
  }

  return "";
};

const extensionFromMimeType = (mimeType) => {
  const mt = String(mimeType || "").toLowerCase().split(";")[0].trim();
  if (!mt) return "";

  const map = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-excel": "xls",
    "text/csv": "csv",
    "application/csv": "csv",
    "application/pdf": "pdf",
    "application/zip": "zip",
    "application/x-zip-compressed": "zip",
    "text/plain": "txt",
    "application/json": "json",
  };

  return map[mt] || "";
};

const ensureFilenameHasExtension = (fileName, mimeType) => {
  const base = String(fileName || "").trim();
  if (!base) return "";

  // If it already has an extension (e.g., name.ext), keep it.
  const lastSegment = base.split("/").pop();
  if (lastSegment && lastSegment.includes(".")) return base;

  const ext = extensionFromMimeType(mimeType);
  if (!ext) return base;
  return `${base}.${ext}`;
};

export default class ApiSession {
  constructor() {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.headers.common["Authorization"] =
      `Bearer ${localStorage.getItem("se-auth0-access-token")}`;
    Axios.defaults.headers.common["token"] = localStorage.getItem(
      "se-auth0-access-token",
    );
    Axios.defaults.headers.common["session-token"] =
      localStorage.getItem("se-auth0-id-token");
    Axios.defaults.headers.common["accept-language"] =
      localStorage.getItem("i18nextLng") ?? "en";
  }
  ashraeBuildingTypeProjectDetails = (projectId, buildingType) => {
    return this.apiGet(
      API_CONFIG.mgaendpoints.ashraeBuildingType,
      {},
      { projectId, buildingType },
    );
  };

  /* to get projects details */
  getProjects = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.mgaProjects, {}, {});
  };
  getScope = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    return this.apiPost(API_CONFIG.endpoints.getScope, {}, {});
  };
  getTariffRawData = (masterTariffId, tariffId, lseId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    // backend expects query params masterTariffId, tariffId and lseId
    return this.apiGet(
      API_CONFIG.mgaendpoints.getTariffRawData,
      {},
      { masterTariffId, tariffId, lseId },
    );
  };

  getMyTariffTemplates = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.myTariffTemplates, {}, {});
  };

  getTariffTemplate = (templateId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.tariffTemplate, {templateId}, {});
  };

  createUpdateTariffTemplate = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.createUpdateTariffTemplate, {}, payload);
  };

  deleteTariffTemplate = (templateId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(API_CONFIG.mgaendpoints.deleteTariffTemplate, { templateId }, {});
  };

  getScopeUser = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    return this.apiPost(API_CONFIG.userMgtendpoints.getScope, {}, {});
  };

  getHealthStatus = () => {
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.mgaendpoints.checkHealthAPI, {}, {});
  };

  importLoad = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.saveComponent, {}, payload);
  };

  getProfile = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.getProfile, {}, payload);
  };

  uploadAndFetchDynamicTariff = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      API_CONFIG.mgaendpoints.uploadAndFetchDynamicTariff,
      {},
      payload,
    );
  };

  getDefaultInfo = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.mgaendpoints.getCurrency, {}, {});
  };
  /* to logout */
  logout = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    return this.apiDelete(API_CONFIG.endpoints.logoutAPI, {}, {});
  };

  // get mgConfigTypeForUser
  getmgConfigTypeForUser = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.mgConfigTypeForUser, {}, {});
  };

  //  get getBuildingTypes
  getBuildingTypes = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.getBuildingTypes, {}, {});
  };

  saveLoadComponent = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.saveComponent, {}, payload);
  };

  runSimulation = (projectId, designId, designVersionId, simulationType) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.runSimulation,
      {},
      { projectId, designId, designVersionId, simulationType },
    );
  };

  simulationStatus = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.simulationStatus,
      {},
      { projectId, designId, designVersionId },
    );
  };

  getkpiFactors = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.kpiFactors,
      {},
      { projectId, designId, designVersionId },
    );
  };

  saveKpiFactors = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiPost(API_CONFIG.mgaendpoints.saveKpiFactors, {}, payload);
  };

  simulationData = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.simulationData,
      {},
      { projectId, designId, designVersionId },
    );
  };

  getSensitivityResultNames = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getSensitivityResultNames,
      {},
      { projectId, designId, designVersionId },
    );
  };

  getSensitivityScenarioData = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getSensitivityScenarioData,
      {},
      { projectId, designId, designVersionId },
    );
  };

  createProject = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.createProject, {}, payload);
  };

  editProject = (projectId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    if (!projectId) {
      return Promise.resolve(null);
    }
    return this.apiGet(
      API_CONFIG.mgaendpoints.editProjectDetails,
      {},
      { projectId },
    );
  };
  // Grid Api
  getProjectList = (page, size) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Default;

    return this.apiGet(API_CONFIG.endpoints.projectList, {}, { page, size });
  };

  getComponentInfo = (designId, designVersionId, componentId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getCompoentDetails,
      {},
      { designId, designVersionId, componentId, includeRateMatrix: false },
    );
  };

  deleteComponent = (designId, designVersionId, componentId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(
      API_CONFIG.mgaendpoints.deleteComponentDetails,
      {},
      { designId, designVersionId, componentId, includeRateMatrix: false },
    );
  };

  cloneVersion = (projectId, designId, designVersionId, payload = {}) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      API_CONFIG.mgaendpoints.cloneVersion,
      { projectId, designId, designVersionId },
      payload,
    );
  };

  saveComments = (ticketId, payload = {}) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      API_CONFIG.mgaendpoints.saveComments,
      { ticketId },
      payload,
    );
  };

  uploadResultImages = (
    projectId,
    designId,
    designVersionId,
    scenarioId,
    file,
  ) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    const formData = new FormData();
    formData.append("file", file, "ResultsImages.zip");
    formData.append("scenarioId", scenarioId);
    formData.append("designVersionId", designVersionId);
    formData.append("designId", designId);
    formData.append("projectId", projectId);
    return this.apiPost(
      API_CONFIG.mgaendpoints.uploadResultImages,
      {},
      formData,
    );
  };

  report = (
    projectId,
    designId,
    designVersionId,
    selectedMicrogridId,
    format,
    parameters,
    scenarioName,
  ) => {
    Axios.defaults.responseType = "arraybuffer";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiPost(
      API_CONFIG.mgaendpoints.report,
      {},
      {
        projectId,
        designId,
        designVersionId,
        scenarioId: selectedMicrogridId,
        format,
        parameters: {},
        scenarioName,
      },
    );
  };

  shareProject = (projectId, email) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    // Backend expects JSON body now
    return this.apiPost(
      API_CONFIG.mgaendpoints.shareProject,
      {},
      { projectId, email },
    );
  };

  copyProject = (currentProjectId, projectName) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      API_CONFIG.mgaendpoints.copyProject,
      {},
      { currentProjectId, projectName },
    );
  };

  getSharedProject = (projectId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getSharedProject,
      {},
      { projectId },
    );
  };

  getContextMenu = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.contextMenu, {}, {});
  };

  getMaintenance = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.maintenance, {}, {});
  };

  saveMaintenance = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.maintenance, {}, payload);
  };

  updateMaintenance = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.maintenance, {}, payload);
  };

  deleteMaintenance = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(API_CONFIG.mgaendpoints.maintenance, {}, {});
  };

  deleteVersion = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(
      API_CONFIG.mgaendpoints.deleteVersion,
      {},
      { projectId, designId, designVersionId },
    );
  };

  deleteProject = (projectId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(
      API_CONFIG.mgaendpoints.deleteProject,
      {},
      { projectId },
    );
  };
  closeTicket = (ticketId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPut(API_CONFIG.mgaendpoints.closeTicket, { ticketId }, {});
  };

  getPvProfile = (projectId, payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      `${API_CONFIG.mgaendpoints.getPvProfile}/${projectId}`,
      {},
      payload,
    );
  };

  getCo2EmissionProfile = (projectId, designId, designVersionId, co2EmissionType) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.co2EmissionProfile,
      {},
      { projectId, designId, designVersionId, co2EmissionType },
    );
  };

  getComponentDefaultValue = (componentType) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.defaultComponentValue,
      {},
      { componentType },
    );
  };
  getPVIncentives = (componentType, country = "GEN") => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.pvIncentive,
      {},
      { componentType, country },
    );
  };
  getDefaultsAssetsAttributes = (assetType) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.defaultAssetsAttributes,
      {},
      { assetType },
    );
  };

  getAssets = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;

    return this.apiGet(API_CONFIG.mgaendpoints.assetsListAPI, {}, {});
  };

  /* to get assets list type*/
  getAssetType = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;

    return this.apiGet(API_CONFIG.mgaendpoints.assetsTypeAPI, {}, {});
  };

  /* to save assets */
  saveAssets = (data) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;

    return this.apiPost(API_CONFIG.mgaendpoints.saveAssetsAPI, {}, data);
  };

  /* to get  asset details */
  getAssetById = (assetId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;

    return this.apiGet(
      API_CONFIG.mgaendpoints.getAssetByIdAPI,
      {},
      { assetId },
    );
  };

  getAssetCatalogue = (assetType) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getAssetCatalogue,
      {},
      { assetType },
    );
  };

  getTimeSeriesData = (projectId, designId, designVersionId, scenarioName) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getTimeSeriesData,
      {},
      { projectId, designId, designVersionId, scenarioName },
    );
  };

  getDownloadImportTemplate = (type) => {
    Axios.defaults.responseType = "arraybuffer";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.getImportTemplate, {}, { type });
  };

  // Downloads import template preserving backend filename (Content-Disposition).
  // Returns: { data: Blob, fileName: string }
  downloadImportTemplate = (type) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    const url = this.parametrizedUri(API_CONFIG.mgaendpoints.getImportTemplate, {});
    return Axios.request({
      method: "get",
      url,
      params: { type },
      responseType: "blob",
    }).then((response) => {
      const headers = response?.headers || {};
      const cd = headers["content-disposition"] || headers["Content-Disposition"];
      const rawName = extractFilenameFromContentDisposition(cd) || `ImportTemplate-${type || "file"}`;
      const safeName = ensureFilenameHasExtension(rawName, response?.data?.type);
      return { data: response.data, fileName: safeName || rawName };
    });
  };

  getSensitivityParams = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getComponentSensitivityParams,
      {},
      { projectId, designId, designVersionId },
    );
  };

  saveProjectSensitivity = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiPost(
      API_CONFIG.mgaendpoints.saveProjectSensitivity,
      {},
      payload,
    );
  };
  saveSensitivityScenarioData = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiPost(
      API_CONFIG.mgaendpoints.saveSensitivityScenarioData,
      {},
      payload,
    );
  };

  getProjectSensitivity = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiGet(
      API_CONFIG.mgaendpoints.getProjectSensitivity,
      {},
      { projectId, designId, designVersionId },
    );
  };

  getGenabilityTariffList = (customerClass, zipCode) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.genabilityTariffList,
      {},
      { customerClass, zipCode },
    );
  };

  getSingleGenabilityTariff = (masterTariffId, tariffId, lseId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.singleGenabilityTariff,
      {},
      { masterTariffId, tariffId, lseId },
    );
  };

  getRegistrationDetails = (token) => {
    Axios.defaults.headers.common["Authorization"] = token;
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    return this.apiGet(API_CONFIG.endpoints.getRegistrationDetails, {}, {});
  };

  saveRegistration = (data, token) => {
    Axios.defaults.headers.common["Authorization"] = token;
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    return this.apiPost(API_CONFIG.endpoints.saveRegistration, {}, data);
  };

  getUsers = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.endpoints.getUsers, {}, {});
  };

  // Ticket Management
  getAllTickets = () => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.getAllTickets, {}, {});
  };

  getTicketId = (ticketId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(API_CONFIG.mgaendpoints.getTicketId, { ticketId }, {});
  };

  getUserProfile = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.userMgtendpoints.profile, {}, {});
  };

  getUserProfileInfo = (email) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    // Standard approach: pass email as query parameter ( /auth/userInfo?email=... )
    // Backend should read from query string now.
    return this.apiGet(API_CONFIG.userMgtendpoints.getUserInfo, {}, { email });
  };

  updateUserProfile = (email, data ) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
      const formData = new FormData();
      formData.append("email", email);
      Object.entries(data)?.forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      return this.apiAny({
        method: "put",
        url: this.parametrizedUri(API_CONFIG.userMgtendpoints.profile, {}),
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
  };

  deleteUser = (userId) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    return this.apiDelete(API_CONFIG.endpoints.deleteUser, {}, { userId });
  };

  getRoles = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.endpoints.getRoles, {}, {});
  };

  getTicketNotification = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.mgaendpoints.ticketNotification, {}, {});
  };

  deleteTicketNotification = (email) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.responseType = "json";
    return this.apiDelete(API_CONFIG.mgaendpoints.ticketNotification, {}, { email });
  };

  saveTicketNotification = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.ticketNotification, {}, payload);
  };

  submitTicket = (payload) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.submitTicket, {}, payload);
  };

  exportTariffConfiguration = (projectId, designId, designVersionId, userEmail, projectTitle) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiPost(API_CONFIG.mgaendpoints.exportTariffConfiguration, {}, { projectId, designId, designVersionId, userEmail, projectTitle });
  };

  deleteSimulation = (projectId, designId, designVersionId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl_Simulation;
    return this.apiDelete(API_CONFIG.mgaendpoints.deleteSimulation, {}, { projectId, designId, designVersionId });
  };

  addUsers = (data) => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl_User_mgt;

    Axios.defaults.responseType = "json";
    return this.apiPost(API_CONFIG.endpoints.addUsers, {}, data);
  };

  getFleets = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.mgaendpoints.fleets, {}, {});
  };

  saveFleets = (projectId, designId, designVersionId, data) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(
      API_CONFIG.mgaendpoints.saveFleets,
      { projectId, designId, designVersionId },
      data,
    );
  };

  getDistributions = () => {
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    Axios.defaults.responseType = "json";
    return this.apiGet(API_CONFIG.mgaendpoints.distributions, {}, {});
  };

  getFleetById = (fleetId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiGet(
      API_CONFIG.mgaendpoints.fleets,
      {},
      { fleetId },
    );
  };

  deleteFleetById = (fleetId) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiDelete(API_CONFIG.mgaendpoints.fleets, {} , { fleetId });
  };

  createDesignVersion = (
    projectId,
    designId,
    designVersionId,
    payload = {},
  ) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    // Use the template and pass params for replacement
    return this.apiPost(
      API_CONFIG.mgaendpoints.createDesignVersion,
      { projectId, designId, designVersionId },
      payload,
    );
  };

  createDemoProject = (payload = {}) => {
    Axios.defaults.responseType = "json";
    Axios.defaults.baseURL = API_CONFIG.baseUrl;
    return this.apiPost(API_CONFIG.mgaendpoints.createDemoProject, {}, payload);
  };

  // API methods
  apiPost = (uriTemplate, uriParameters, data) => {
    const url = this.parametrizedUri(uriTemplate, uriParameters);
    return this.apiAny({ method: "post", url, data });
  };

  apiPut = (uriTemplate, uriParameters, data) => {
    const url = this.parametrizedUri(uriTemplate, uriParameters);
    return this.apiAny({ method: "put", url, data });
  };

  apiGet = (uriTemplate, uriParameters, params) => {
    // Axios.defaults.baseURL = API_CONFIG.baseUrl;
    const url = this.parametrizedUri(uriTemplate, uriParameters);
    return this.apiAny({ method: "get", url, params });
  };
  apiDelete = (uriTemplate, uriParameters, params) => {
    const url = this.parametrizedUri(uriTemplate, uriParameters);
    return this.apiAny({ method: "delete", url, params });
  };

  parametrizedUri = (uriTemplate, parameters) => {
    return _.reduce(
      parameters,
      (uri, value, key) => {
        return _.replace(uri, new RegExp(`{${key}}`), value);
      },
      uriTemplate,
    );
  };

  apiAny = ({ method, url, params, data }) => {
    let result = Promise.resolve(url);
    result.cancel = _.noop;
    if (_.isString(url)) {
      const cancelSource = Axios.CancelToken.source();
      const cancelToken = cancelSource.token;
      result = Axios.request({ method, url, params, data, cancelToken }).then(
        _.property("data"),
      );
      result.cancel = cancelSource.cancel;
    }
    result
      .then((response) => {
        //do nothing
      })
      .catch((error) => {
        if (
          localStorage.getItem("se-auth0-access-token") === null &&
          window.location?.pathname !== "/registration"
        ) {
          window.location.replace("/");
        }
        try {
          if (error?.response !== undefined) {
            if (error?.response?.data !== undefined) {
              if (error?.response?.data !== null) {
                if (
                  (error?.response?.data?.httpCodeMessage === "Forbidden" &&
                    error?.response?.data?.message === "Unauthorized") ||
                  error?.message?.includes("403") ||
                  (typeof error?.response?.data === "object" &&
                    error?.response?.data?.status === "UNAUTHORIZED") ||
                  error?.response?.data?.includes("Unauthorized")
                ) {
                  window.location.replace("/");
                }
              }
            }
          }
        } catch (error) {
          console.log("ERROR");
        }
      });
    return result;
  };

  safeInvoke = (fn, ...args) => {
    try {
      const result = fn.apply(this, args);
      if (result && typeof result.then === "function") return result;
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
